import { useCallback, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { selectErrorsForKeys, selectIsFetchingForKeys } from '@/reducers/fetch'
import {
  fetchSubscriptions,
  selectHeardProductPlanInterval,
  selectHeardProductPlanPrice,
  StripeInterval,
} from '@/reducers/subscription.slice'
import {
  getCurrentUser,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import {
  submitCancelStripeSubscriptionAtPeriodEnd,
  SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY,
} from '@/actions/settings/billingActions'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  centsToCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import {
  APPLY_CANCELLATION_DISCOUNT_KEY,
  applyCancellationDiscount,
  CancellationStepIdentifier,
  updateAutomaticCancellationAnalyticsRecord,
} from './lib/userCancellation.slice'
import {
  CANCELLATION_FLOW_STEP,
  getDefaultPrice,
  useSelfServiceCancellationSteps,
} from './lib/helpers'

import { TypographyStyles, TextColors } from '@/styles'
import { Alert, Button, Card, CardColors, Image, Badge, Text } from '@/ui'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

export const DiscountOffer = ({ recordId }: { recordId?: number }) => {
  const dispatch = useAppDispatch()
  const [_, setSearchParams] = useSearchParams()
  const location = useLocation()
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)
  const currentUser = useReselector(getCurrentUser)
  const userPrice = useReselector(selectHeardProductPlanPrice)

  const error = useReselector(selectErrorsForKeys, [
    APPLY_CANCELLATION_DISCOUNT_KEY,
    SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY,
  ])

  useEffect(() => {
    const updateRecord = async (recordId?: number) => {
      if (recordId) {
        await updateAutomaticCancellationAnalyticsRecord(recordId, {
          finalStepReached: CancellationStepIdentifier.DISCOUNT,
        })(dispatch)
      }
    }

    updateRecord(recordId)
  }, [dispatch, recordId])

  useEffect(() => {
    dispatch(fetchSubscriptions())
  }, [dispatch])

  const goToNextStep = useSelfServiceCancellationSteps(
    CANCELLATION_FLOW_STEP.discountOffer
  )

  const getSavedAmount = (
    isMonthly: boolean,
    isScorp: boolean,
    userPrice: number | null
  ) => {
    const defaultPrice = getDefaultPrice(isMonthly, isScorp)
    const currentPrice = centsToCurrency(userPrice ?? defaultPrice)

    if (isMonthly) {
      // for monthly plans, we offer 50% off for 3 months
      return currentPrice.multiply(1.5)
    } else {
      // for annual plans, we offer 20% off their next plan
      return currentPrice.multiply(0.2)
    }
  }

  const currentSubscriptionInterval =
    useReselector(selectHeardProductPlanInterval) ?? StripeInterval.month

  const isMonthly = currentSubscriptionInterval === StripeInterval.month
  const isScorp = useReselector(selectIsCurrentUserScorp)

  const dollarsSavedText = formatCurrencyFromCents(
    getSavedAmount(isMonthly, isScorp, userPrice),
    { hideDecimalsIfZero: true }
  )

  const amountSavedText = isMonthly
    ? 'Get 50% off for the next 3 months'
    : 'Get 20% off your next renewal'

  const amountSavedSubtext = isMonthly
    ? 'As a one time offer, get 50% off for the next 3 months of the cost of your Heard membership on us.'
    : 'As a one time offer, get a 20% discount on your annual subscription*.'

  const isApplyingDiscountLoading = useReselector(selectIsFetchingForKeys, [
    APPLY_CANCELLATION_DISCOUNT_KEY,
  ])

  const acceptDiscount = useCallback(async () => {
    const res = await applyCancellationDiscount()(dispatch)

    if (res) {
      if (recordId) {
        await updateAutomaticCancellationAnalyticsRecord(recordId, {
          discountApplied: true,
        })(dispatch)
      }

      setSearchParams(
        { step: CANCELLATION_FLOW_STEP.discountComplete },
        { state: { recordId } }
      )
    }
  }, [dispatch, setSearchParams, recordId])

  const onContinue = useCallback(async () => {
    const res = await submitCancelStripeSubscriptionAtPeriodEnd(
      location?.state?.cancellationDetails ?? {}
    )(dispatch)

    if (res) {
      sendUserCancellationMessageZap(currentUser, 'submit_cancel')
      goToNextStep()
    }
  }, [location, currentUser, goToNextStep, dispatch])

  return (
    <div className="flex flex-col items-center space-y-6">
      {error.length > 0 && (
        <Alert title="Error" description={error[0].message} variant="error" />
      )}

      <Image
        alt="gift box"
        src="https://heard-images.s3.amazonaws.com/assets/giftbox.svg"
        width={180}
        height={180}
      />

      <Text variant={TypographyStyles.H1}>Before you go...</Text>

      <Text
        variant={TypographyStyles.BODY_LG}
        className="w-full md:w-1/2 text-center"
      >
        We&apos;re always working to improve Heard to address our{' '}
        {replacementText}
        community&apos;s feedback. We would love for you to stay with us so you
        can enjoy even more benefits with your membership.
      </Text>

      <Card className="w-full md:w-5/8 mx-auto" color={CardColors.EMPHASIS}>
        <div className="flex">
          <div className="w-5/8 space-y-4">
            <Badge color="gray" variant="default">
              Save {dollarsSavedText}
            </Badge>

            <Text variant={TypographyStyles.H2}>{amountSavedText}</Text>
            <Text variant={TypographyStyles.BODY_MD}>{amountSavedSubtext}</Text>

            {!isMonthly && (
              <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
                *Discount will apply when your subscription renews.
              </Text>
            )}
          </div>

          <div className="w-3/8 flex items-center">
            <Button
              loading={isApplyingDiscountLoading}
              onClick={() => acceptDiscount()}
            >
              Accept offer
            </Button>
          </div>
        </div>
      </Card>

      <Button variant="link" onClick={() => onContinue()}>
        No thanks, Finalize Cancellation
      </Button>
    </div>
  )
}
